import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import {
  selectActiveLayers,
  selectMapData,
  toggleLayer
} from "../redux/global-map.slice"
import { MAP_LAYERS } from "../redux/global-map.types"
import type { MapLayer } from "../redux/global-map.types"
import { EntityIcon } from "./EntityIcon.tsx"

// Lead, Project, Subcontractor, Appointment — the same order the KPI row
// above the map lists them in.
const LAYER_ORDER: MapLayer[] = MAP_LAYERS

// Whole class strings per layer (Tailwind only sees literals), each in that
// layer's own colour — the same four the markers, the KPI tiles and the
// entity glyphs use.
const ACTIVE_CLASS: Record<MapLayer, string> = {
  lead: "border-blue-300 bg-blue-50 text-blue-800",
  project: "border-green-300 bg-green-50 text-green-800",
  subcontractor: "border-purple-300 bg-purple-50 text-purple-800",
  appointment: "border-sky-300 bg-sky-50 text-sky-800"
}

/**
 * The map's own layer control, floating bottom-left where the static legend
 * used to sit.
 *
 * It replaces that legend rather than joining it: the legend said what the
 * markers mean but not which layers were actually on, so a map with Leads
 * switched off looked identical to one with no leads in view. Each entry now
 * carries its own on/off state — the entity's colour, tint and full-strength
 * icon when on; neutral, dimmed and greyscale when off — so the state reads
 * without clicking, while still naming what each marker is.
 *
 * There is ONE source of truth. These buttons dispatch the same `toggleLayer`
 * action against the same `activeLayers` state the toolbar's Lead/Project
 * pills read and write, so the two controls cannot disagree and no second
 * filter state exists to drift. Toggling here updates the pills above, and
 * vice versa, for free.
 *
 * All four layers the map can draw appear here. Subcontractor and
 * Appointment markers are drawn on the records they hang off (an
 * assignment's project, an appointment's lead/project), so toggling either
 * adds or removes real markers — never a control that filters nothing. "At
 * Risk (SLA)" is deliberately absent: no such marker exists to hide.
 */
export function MapLayerControls() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const activeLayers = useAppSelector(selectActiveLayers)
  // Layers the server refused for this response (`fetchMapFeatures` asked
  // again without them, so the rest of the map still answered). A layer the
  // backend cannot draw must not read as switched on with nothing on the map
  // — it says so instead, and recovers on its own once a backend that knows
  // it answers, since the layer never leaves the shared state.
  const unavailableLayers = useAppSelector(selectMapData)?.unavailable_layers

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    entityType: MapLayer
  ) => {
    // jsdom does not turn a focused Enter/Space into a click the way a real
    // browser does, so the handler drives the toggle directly; `preventDefault`
    // stops a real keypress double-toggling.
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault()
      dispatch(toggleLayer(entityType))
    }
  }

  return (
    <div
      role="group"
      aria-label={t("globalMap.layers.groupLabel")}
      className="absolute bottom-4 left-4 z-10 flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] bg-white/95 p-1.5 shadow-[0_4px_12px_rgba(15,23,42,0.1)] backdrop-blur"
      data-testid="global-map-legend"
    >
      {LAYER_ORDER.map((entityType) => {
        const unavailable = Boolean(unavailableLayers?.includes(entityType))
        const active = activeLayers.includes(entityType) && !unavailable
        const label = t(`globalMap.entityLabel.${entityType}`)
        return (
          <button
            key={entityType}
            type="button"
            aria-pressed={active}
            // A distinct accessible name from the toolbar's own Lead/Project
            // pill: both drive the same state, so leaving them identically
            // named left two "Project" buttons on the page with no way for a
            // screen-reader user — or a test — to tell them apart.
            aria-label={
              unavailable
                ? t("globalMap.layers.unavailableLabel", { entity: label })
                : t("globalMap.layers.toggleLabel", { entity: label })
            }
            title={
              unavailable ? t("globalMap.layers.unavailableHint") : undefined
            }
            // `aria-disabled`, never `disabled`: a disabled button leaves the
            // tab order, so the one explanation of why this layer is off could
            // not be reached from the keyboard.
            aria-disabled={unavailable || undefined}
            onClick={() => {
              if (!unavailable) dispatch(toggleLayer(entityType))
            }}
            onKeyDown={(event) => {
              if (unavailable) return
              handleKeyDown(event, entityType)
            }}
            data-testid={`global-map-layer-${entityType}`}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-[13px] font-medium transition-colors",
              active
                ? ACTIVE_CLASS[entityType]
                : "border-transparent bg-transparent text-slate-400 hover:bg-slate-50",
              unavailable && "cursor-not-allowed opacity-60 hover:bg-transparent"
            )}
          >
            {/* The marker's own swatch, so the control names exactly what is
                drawn on the tiles. Desaturated while the layer is off. */}
            <EntityIcon
              entityType={entityType}
              className={cn("size-[18px]", !active && "opacity-40 grayscale")}
            />
            {label}
          </button>
        )
      })}
    </div>
  )
}

export default MapLayerControls
